import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { 
  ArrowRight, ArrowLeft, Save, Calendar, 
  MessageSquare, Eye, Brain, Target, CheckCircle,
  ChevronDown, ChevronUp
} from "lucide-react";
import { RELAZIONI, STATI_EMOTIVI, VOLUMI, RITMI, EMOZIONI, STILI_RISPOSTA, TOTAL_SECTIONS } from "./SchedaConstants";

const SectionHeader = ({ number, title, icon: Icon, expandedSection, setExpandedSection }) => (
  <button
    onClick={() => setExpandedSection(expandedSection === number ? 0 : number)}
    className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
  >
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-sm">
        {number}
      </div>
      <Icon className="w-5 h-5 text-gray-500" />
      <span className="font-medium text-gray-700">{title}</span>
    </div>
    {expandedSection === number ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
  </button>
);

const NavigationButtons = ({ currentSection, goToPrevSection, goToNextSection, onSave, loading }) => (
  <div className="flex justify-between items-center pt-4 mt-4 border-t border-gray-100">
    {currentSection > 1 ? (
      <Button variant="outline" size="sm" onClick={goToPrevSection} className="rounded-full">
        <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
      </Button>
    ) : <div />}
    {currentSection < TOTAL_SECTIONS ? (
      <Button size="sm" onClick={goToNextSection} className="rounded-full bg-indigo-600 hover:bg-indigo-700">
        Avanti <ArrowRight className="w-4 h-4 ml-1" />
      </Button>
    ) : (
      <Button size="sm" onClick={onSave} disabled={loading} className="rounded-full bg-gradient-to-r from-orange-500 to-amber-600">
        <Save className="w-4 h-4 mr-1" /> Salva
      </Button>
    )}
  </div>
);

const ToggleChips = ({ items, value, onChange, activeClass = "bg-indigo-600 text-white" }) => (
  <div className="flex flex-wrap gap-2">
    {items.map(item => (
      <button
        key={item.id}
        onClick={() => onChange(value === item.id ? "" : item.id)}
        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
          value === item.id ? activeClass : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        {item.label}
      </button>
    ))}
  </div>
);

const NonVerbaleBlock = ({ prefix, label, bgClass, activeClass, form, setForm }) => (
  <div className={`p-3 ${bgClass} rounded-xl space-y-3`}>
    <h4 className={`font-medium ${prefix === "io" ? "text-blue-800" : "text-orange-800"}`}>{label}</h4>
    <div>
      <label className="text-xs text-gray-600">Stato emotivo</label>
      <div className="flex flex-wrap gap-2 mt-1">
        {STATI_EMOTIVI.map(s => (
          <button key={s.id}
            onClick={() => setForm({...form, [`${prefix}StatoEmotivo`]: form[`${prefix}StatoEmotivo`] === s.id ? "" : s.id})}
            className={`px-2 py-1 rounded-lg text-xs ${form[`${prefix}StatoEmotivo`] === s.id ? activeClass : 'bg-white text-gray-700'}`}
          >{s.label}</button>
        ))}
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className="text-xs text-gray-600">Volume</label>
        <div className="flex gap-1 mt-1">
          {VOLUMI.map(v => (
            <button key={v.id}
              onClick={() => setForm({...form, [`${prefix}Volume`]: form[`${prefix}Volume`] === v.id ? "" : v.id})}
              className={`flex-1 px-2 py-1 rounded-lg text-xs ${form[`${prefix}Volume`] === v.id ? activeClass : 'bg-white text-gray-700'}`}
            >{v.label}</button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-xs text-gray-600">Ritmo</label>
        <div className="flex gap-1 mt-1">
          {RITMI.map(r => (
            <button key={r.id}
              onClick={() => setForm({...form, [`${prefix}Ritmo`]: form[`${prefix}Ritmo`] === r.id ? "" : r.id})}
              className={`flex-1 px-2 py-1 rounded-lg text-xs ${form[`${prefix}Ritmo`] === r.id ? activeClass : 'bg-white text-gray-700'}`}
            >{r.label}</button>
          ))}
        </div>
      </div>
    </div>
    <div>
      <label className="text-xs text-gray-600">Postura/sguardo</label>
      <Input 
        value={form[`${prefix}Postura`]} 
        onChange={e => setForm({...form, [`${prefix}Postura`]: e.target.value})}
        placeholder={prefix === "io" ? "Es: braccia conserte, sguardo basso..." : "Es: voce alta, gesticolava..."}
        className="rounded-lg mt-1"
      />
    </div>
  </div>
);

export const SchedaFormView = ({ form, setForm, editingId, expandedSection, setExpandedSection, loading, onSave }) => {
  const goToNextSection = () => {
    if (expandedSection < TOTAL_SECTIONS) {
      setExpandedSection(expandedSection + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  const goToPrevSection = () => {
    if (expandedSection > 1) {
      setExpandedSection(expandedSection - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const updateArrayField = (field, index, value) => {
    const newArr = [...form[field]];
    newArr[index] = value;
    setForm({ ...form, [field]: newArr });
  };

  const toggleEmozione = (emozioneId, intensity) => {
    const newEmozioni = { ...form.emozioni };
    if (intensity === 0 || newEmozioni[emozioneId] === intensity) {
      delete newEmozioni[emozioneId];
    } else {
      newEmozioni[emozioneId] = intensity;
    }
    setForm({ ...form, emozioni: newEmozioni });
  };

  const navProps = { goToPrevSection, goToNextSection, onSave, loading };

  return (
    <div className="space-y-4 pb-24">
      <div className="text-center">
        <h2 className="font-semibold text-sm">{editingId ? "Modifica Scheda" : "Nuova Scheda"}</h2>
        <p className="text-xs text-gray-500">Sezione {expandedSection} di {TOTAL_SECTIONS}</p>
      </div>

      <div className="w-full bg-gray-200 rounded-full h-1.5">
        <div className="bg-orange-500 h-1.5 rounded-full transition-all" style={{ width: `${(expandedSection / TOTAL_SECTIONS) * 100}%` }} />
      </div>

      {/* Sezione 1 */}
      <div className="space-y-3">
        <SectionHeader number={1} title="Dati essenziali" icon={Calendar} expandedSection={expandedSection} setExpandedSection={setExpandedSection} />
        {expandedSection === 1 && (
          <Card><CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600">Data</label>
                <Input type="date" value={form.data} onChange={e => setForm({...form, data: e.target.value})} className="rounded-lg" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Con chi</label>
                <Input value={form.conChi} onChange={e => setForm({...form, conChi: e.target.value})} placeholder="Nome/ruolo" className="rounded-lg" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Luogo/Contesto</label>
              <Input value={form.luogo} onChange={e => setForm({...form, luogo: e.target.value})} placeholder="Es: casa, ufficio, telefono..." className="rounded-lg" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-2 block">Relazione</label>
              <ToggleChips items={RELAZIONI} value={form.relazione} onChange={v => setForm({...form, relazione: v})} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Tema/Argomento</label>
              <Input value={form.tema} onChange={e => setForm({...form, tema: e.target.value})} placeholder="Di cosa si trattava?" className="rounded-lg" />
            </div>
            <NavigationButtons currentSection={1} {...navProps} />
          </CardContent></Card>
        )}
      </div>

      {/* Sezione 2 */}
      <div className="space-y-3">
        <SectionHeader number={2} title="Cosa è accaduto" icon={MessageSquare} expandedSection={expandedSection} setExpandedSection={setExpandedSection} />
        {expandedSection === 2 && (
          <Card><CardContent className="p-4">
            <label className="text-xs font-medium text-gray-600 mb-2 block">Descrivi in 3 righe cosa è successo (solo fatti, senza interpretazioni)</label>
            <Textarea value={form.descrizione} onChange={e => setForm({...form, descrizione: e.target.value})} placeholder="Cosa è successo..." rows={4} className="rounded-lg" />
            <NavigationButtons currentSection={2} {...navProps} />
          </CardContent></Card>
        )}
      </div>

      {/* Sezione 3 */}
      <div className="space-y-3">
        <SectionHeader number={3} title="Cosa ho detto io" icon={MessageSquare} expandedSection={expandedSection} setExpandedSection={setExpandedSection} />
        {expandedSection === 3 && (
          <Card><CardContent className="p-4 space-y-3">
            <label className="text-xs font-medium text-gray-600">Parole testuali (scrivi tra virgolette)</label>
            {[0, 1, 2].map(i => (
              <Input key={i} value={form.ioDetto[i]} onChange={e => updateArrayField("ioDetto", i, e.target.value)} placeholder={'"..."'} className="rounded-lg" />
            ))}
            <NavigationButtons currentSection={3} {...navProps} />
          </CardContent></Card>
        )}
      </div>

      {/* Sezione 4 */}
      <div className="space-y-3">
        <SectionHeader number={4} title="Cosa ha detto l'altro" icon={MessageSquare} expandedSection={expandedSection} setExpandedSection={setExpandedSection} />
        {expandedSection === 4 && (
          <Card><CardContent className="p-4 space-y-3">
            <label className="text-xs font-medium text-gray-600">Parole testuali (scrivi tra virgolette)</label>
            {[0, 1, 2].map(i => (
              <Input key={i} value={form.altroDetto[i]} onChange={e => updateArrayField("altroDetto", i, e.target.value)} placeholder={'"..."'} className="rounded-lg" />
            ))}
            <NavigationButtons currentSection={4} {...navProps} />
          </CardContent></Card>
        )}
      </div>

      {/* Sezione 5 - Non verbale */}
      <div className="space-y-3">
        <SectionHeader number={5} title="Non verbale e tono" icon={Eye} expandedSection={expandedSection} setExpandedSection={setExpandedSection} />
        {expandedSection === 5 && (
          <Card><CardContent className="p-4 space-y-4">
            <NonVerbaleBlock prefix="io" label="IO" bgClass="bg-blue-50" activeClass="bg-blue-600 text-white" form={form} setForm={setForm} />
            <NonVerbaleBlock prefix="altro" label="L'ALTRO" bgClass="bg-orange-50" activeClass="bg-orange-600 text-white" form={form} setForm={setForm} />
            <NavigationButtons currentSection={5} {...navProps} />
          </CardContent></Card>
        )}
      </div>

      {/* Sezione 6 - Emozioni */}
      <div className="space-y-3">
        <SectionHeader number={6} title="Come mi sono sentito" icon={Brain} expandedSection={expandedSection} setExpandedSection={setExpandedSection} />
        {expandedSection === 6 && (
          <Card><CardContent className="p-4 space-y-4">
            <label className="text-xs font-medium text-gray-600">Seleziona 1-3 emozioni e indica l'intensità (1-10)</label>
            <div className="grid grid-cols-2 gap-3">
              {EMOZIONI.map(em => (
                <div key={em.id} className={`p-3 rounded-xl border-2 transition-all ${form.emozioni[em.id] ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm">{em.emoji} {em.label}</span>
                    {form.emozioni[em.id] && <Badge className="bg-indigo-600">{form.emozioni[em.id]}</Badge>}
                  </div>
                  <Slider value={[form.emozioni[em.id] || 0]} onValueChange={([v]) => toggleEmozione(em.id, v)} min={0} max={10} step={1} className="w-full" />
                </div>
              ))}
            </div>
            <div>
              <label className="text-xs text-gray-600">Sensazioni fisiche (se presenti)</label>
              <Input value={form.sensazioniFisiche} onChange={e => setForm({...form, sensazioniFisiche: e.target.value})} placeholder="Es: nodo alla gola, mani sudate..." className="rounded-lg mt-1" />
            </div>
            <NavigationButtons currentSection={6} {...navProps} />
          </CardContent></Card>
        )}
      </div>

      {/* Sezione 7 - Pensieri */}
      <div className="space-y-3">
        <SectionHeader number={7} title="Pensieri in testa" icon={Brain} expandedSection={expandedSection} setExpandedSection={setExpandedSection} />
        {expandedSection === 7 && (
          <Card><CardContent className="p-4 space-y-3">
            <label className="text-xs font-medium text-gray-600">Scrivi 1-3 pensieri così come sono apparsi, senza rielaborarli</label>
            {[0, 1, 2].map(i => (
              <Input key={i} value={form.pensieri[i]} onChange={e => updateArrayField("pensieri", i, e.target.value)} placeholder={`"Pensiero ${i + 1}..."`} className="rounded-lg" />
            ))}
            <NavigationButtons currentSection={7} {...navProps} />
          </CardContent></Card>
        )}
      </div>

      {/* Sezione 8 - Obiettivi */}
      <div className="space-y-3">
        <SectionHeader number={8} title="Obiettivi e risultati" icon={Target} expandedSection={expandedSection} setExpandedSection={setExpandedSection} />
        {expandedSection === 8 && (
          <Card><CardContent className="p-4 space-y-4">
            <div>
              <label className="text-xs font-medium text-gray-600">Cosa volevo ottenere</label>
              <Textarea value={form.cosaVolevo} onChange={e => setForm({...form, cosaVolevo: e.target.value})} placeholder="Qual era il mio obiettivo..." rows={2} className="rounded-lg mt-1" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Cosa ho ottenuto</label>
              <Textarea value={form.cosaOttenuto} onChange={e => setForm({...form, cosaOttenuto: e.target.value})} placeholder="Cosa è successo alla fine..." rows={2} className="rounded-lg mt-1" />
            </div>
            <NavigationButtons currentSection={8} {...navProps} />
          </CardContent></Card>
        )}
      </div>

      {/* Sezione 9 - Stile */}
      <div className="space-y-3">
        <SectionHeader number={9} title="Stile comunicativo" icon={CheckCircle} expandedSection={expandedSection} setExpandedSection={setExpandedSection} />
        {expandedSection === 9 && (
          <Card><CardContent className="p-4 space-y-4">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-2 block">La MIA risposta è stata:</label>
              <div className="flex flex-wrap gap-2">
                {STILI_RISPOSTA.map(s => (
                  <button key={s.id}
                    onClick={() => setForm({...form, mioStile: form.mioStile === s.id ? "" : s.id})}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${form.mioStile === s.id ? s.color + ' ring-2 ring-offset-2 ring-gray-400' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >{s.label}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-2 block">La risposta dell'ALTRO è stata:</label>
              <div className="flex flex-wrap gap-2">
                {STILI_RISPOSTA.map(s => (
                  <button key={s.id}
                    onClick={() => setForm({...form, altroStile: form.altroStile === s.id ? "" : s.id})}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${form.altroStile === s.id ? s.color + ' ring-2 ring-offset-2 ring-gray-400' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >{s.label}</button>
                ))}
              </div>
            </div>
            <NavigationButtons currentSection={9} {...navProps} />
          </CardContent></Card>
        )}
      </div>

      {/* Save Button */}
      <div className="fixed bottom-4 left-4 right-4 max-w-2xl mx-auto">
        <Button onClick={onSave} disabled={loading}
          className="w-full rounded-xl bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 shadow-lg" size="lg">
          <Save className="w-5 h-5 mr-2" />
          {loading ? "Salvataggio..." : (editingId ? "Aggiorna Scheda" : "Salva Scheda")}
        </Button>
      </div>
    </div>
  );
};
